import { useQuery } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { agentDefsApi } from '../api/agents'
import { useStreamStore } from '../store/stream'
import type { AgentDefInfo } from '../types/api'
import { agentQueryKeys, useWaitingQuery } from './queries'

/** Derived live state of one agent (redesign concept model: role × state;
 * role is per-topic and rendered in the topic view, the rail shows only
 * the global state). Precedence: paused (definition) > blocked (has an
 * unresolved submitted review item — from the waiting list) > running
 * (has an active turn) > idle. Derived client-side from existing
 * payloads; promote to a server field if drift appears. */
export type AgentLiveState = 'paused' | 'blocked' | 'running' | 'idle'

/**
 * Shared agent live-state derivation for the flat rail and the topic
 * view's agent header — one definition of "what state is this agent in",
 * fed by the workspace agents query, the waiting-on-you list (blocked),
 * and the stream store's authoritative active turns (running).
 *
 * Disabled until a workspace id is known (topic routes resolve theirs
 * through the topic query first); `stateOf` answers `idle` meanwhile.
 */
export function useAgentLiveStates(workspaceId: string | undefined) {
  const { data: agents = [] } = useQuery<AgentDefInfo[]>({
    queryKey: agentQueryKeys.workspace(workspaceId ?? ''),
    queryFn: () =>
      workspaceId
        ? agentDefsApi.listForWorkspace(workspaceId)
        : Promise.resolve([]),
    enabled: !!workspaceId,
    staleTime: 60_000,
  })
  const { data: waiting } = useWaitingQuery(workspaceId ?? '', !!workspaceId)

  const activeTurns = useStreamStore((s) => s.activeTurns)
  const activeTurnAgentIds = useMemo(() => {
    const ids = new Set<string>()
    for (const key in activeTurns)
      for (const agentId of activeTurns[key] ?? []) ids.add(agentId)
    return ids
  }, [activeTurns])
  const blockedAgentIds = useMemo(() => {
    const ids = new Set<string>()
    for (const item of waiting?.items ?? [])
      if (item.agent_id) ids.add(item.agent_id)
    return ids
  }, [waiting])

  const stateOf = useCallback(
    (agentId: string): AgentLiveState => {
      const def = agents.find((a) => a.agent_id === agentId)
      if (def?.status === 'paused') return 'paused'
      if (blockedAgentIds.has(agentId)) return 'blocked'
      if (activeTurnAgentIds.has(agentId)) return 'running'
      return 'idle'
    },
    [agents, blockedAgentIds, activeTurnAgentIds],
  )

  return {
    agents,
    stateOf,
    activeTurnAgentIds,
    /** Unresolved review items workspace-wide (rail badge count). */
    waitingCount: waiting?.items.length ?? 0,
  }
}
